import { useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Button, IconButton, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import type { Dashboard } from "../types";
import { CATEGORY, clock, FeedIcon, PendingIcon } from "./FeedIcon";

const Root = styled(Paper)(({ theme }) => ({ padding: theme.spacing(2, 2.5) }));

const Heading = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));

const Title = styled(Typography)(() => ({ fontSize: "1rem", fontWeight: 700 }));

const List = styled(Box)(() => ({ display: "flex", flexDirection: "column" }));

/** One step: icon column (with the connector line to the next step), time,
 *  then what happened and who recorded it. */
const Step = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "26px 44px minmax(0, 1fr)",
  columnGap: theme.spacing(1.5),
  position: "relative",
  paddingBottom: theme.spacing(1.5),
  // The connector: from under this icon to the next one.
  "&:not(:last-of-type)::before": {
    content: '""',
    position: "absolute",
    left: 12,
    top: 28,
    bottom: 0,
    width: 2,
    background: theme.palette.divider,
  },
}));

const Time = styled(Typography)(() => ({
  fontSize: "0.875rem",
  fontWeight: 600,
  lineHeight: "26px",
  fontVariantNumeric: "tabular-nums",
}));

const What = styled(Box)(() => ({ minWidth: 0, paddingTop: 3 }));

const StepTitle = styled(Typography)(() => ({ fontSize: "0.875rem", fontWeight: 700, lineHeight: 1.35 }));

const Sub = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  overflowWrap: "anywhere",
}));

const Empty = styled(Typography)(({ theme }) => ({ color: theme.palette.text.secondary, fontSize: "0.875rem" }));

/** How many of the latest steps show before "View full timeline". */
const COLLAPSED = 8;

type MissionTimelineProps = {
  dashboard: Dashboard;
};

/**
 * The mission's key moments, oldest first, ending in the step still pending.
 * Built server-side from recorded facts only (dashboard.milestones): every
 * reading after the first of each vital lives in Recent Updates instead.
 */
export function MissionTimeline(props: MissionTimelineProps) {
  const { dashboard } = props;
  const [expanded, setExpanded] = useState(false);
  const [menu, setMenu] = useState<HTMLElement | null>(null);

  const all = dashboard.milestones;
  const hidden = expanded ? 0 : Math.max(0, all.length - COLLAPSED);
  const shown = all.slice(hidden);

  // The next step, when the record says what it is.
  let pending: { at: string | null; title: string; detail: string } | null = null;
  if (dashboard.eta_minutes !== null && dashboard.phase !== "complete") {
    const arrival = new Date(new Date(dashboard.updated_at).getTime() + dashboard.eta_minutes * 60000);
    pending = { at: arrival.toISOString(), title: "Arrival pending", detail: `ETA ${dashboard.eta_minutes} min` };
  } else if (dashboard.phase !== "complete" && all.length > 0) {
    pending = { at: null, title: "In progress", detail: "No destination or ETA recorded yet" };
  }

  return (
    <Root>
      <Heading>
        <Title>Mission Timeline</Title>
        <IconButton size="small" aria-label="Timeline options" onClick={(e) => setMenu(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          <MenuItem
            onClick={() => {
              setExpanded(!expanded);
              setMenu(null);
            }}
          >
            {expanded ? "Show latest steps only" : "Show full timeline"}
          </MenuItem>
        </Menu>
      </Heading>

      {all.length === 0 ? (
        <Empty>Nothing recorded yet.</Empty>
      ) : (
        <List>
          {shown.map((item) => (
            <Step key={item.id}>
              <FeedIcon category={item.category} icon={item.icon} />
              <Time>{clock(item.at)}</Time>
              <What>
                <StepTitle>{item.title}</StepTitle>
                {item.detail ? <Sub>{item.detail}</Sub> : null}
                <Sub>{CATEGORY[item.category].label}</Sub>
              </What>
            </Step>
          ))}
          {pending ? (
            <Step>
              <PendingIcon />
              <Time>{pending.at ? clock(pending.at) : ""}</Time>
              <What>
                <StepTitle>{pending.title}</StepTitle>
                <Sub>{pending.detail}</Sub>
              </What>
            </Step>
          ) : null}
        </List>
      )}

      {all.length > COLLAPSED ? (
        <Button fullWidth variant="outlined" onClick={() => setExpanded(!expanded)} sx={{ mt: 0.5 }}>
          {expanded ? "Show latest steps" : `View full timeline (${all.length})`}
        </Button>
      ) : null}
    </Root>
  );
}
